import React, { useEffect, useMemo, useState } from 'react';
import { Download, Plus, RefreshCw, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { chargeMapAdminService, type UnmappedItem, type UnmappedReport } from '../../services/chargeMapAdminService';
import { downloadCsv, toCsv } from '../../../../../features/billValidation/lib/csvText';

type UnmappedItemsTabProps = {
  /** Opens the Add mapping dialog pre-filled for this item. */
  onMap: (item: UnmappedItem) => void;
};

const SELECT_CLASS =
  'h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const CSV_HEADERS = [
  'NMI', 'Retailer', 'Distributor', 'Tariff', 'State', 'Section', 'Bill Item', 'Amount', 'Bill From', 'Bill To', 'Bill Link',
] as const;

const money = (n: number) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
const NMI_PREVIEW = 3;

/**
 * Job 93: every invoice line the charge map does not recognise, across all
 * active bills — the same lines the Comparison tab badges "Unmapped". Grouped by
 * retailer + section + wording so one gap shows once however many bills carry
 * it; the CSV has one row per bill line, NMI included, as requested.
 */
export const UnmappedItemsTab: React.FC<UnmappedItemsTabProps> = ({ onMap }) => {
  const [report, setReport] = useState<UnmappedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [search, setSearch] = useState('');
  const [retailer, setRetailer] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    chargeMapAdminService.unmapped().then((res) => {
      if (!active) return;
      if (res.success) setReport(res.data ?? null);
      else setError(res.error ?? 'Failed to load unmapped items');
      setLoading(false);
    });
    return () => { active = false; };
  }, [reloadToken]);

  const retailers = useMemo(
    () => [...new Set((report?.items ?? []).map((i) => i.retailer ?? 'Unknown retailer'))].sort(),
    [report],
  );

  const q = search.trim().toLowerCase();
  const matches = (r: string | null, item: string, nmis: Array<string | null>) =>
    (!retailer || (r ?? 'Unknown retailer') === retailer)
    && (!q || item.toLowerCase().includes(q) || nmis.some((n) => (n ?? '').toLowerCase().includes(q)));

  const items = (report?.items ?? []).filter((i) => matches(i.retailer, i.bill_item, i.nmis));

  const handleExport = () => {
    const lines = (report?.lines ?? []).filter((l) => matches(l.retailer, l.bill_item, [l.nmi]));
    const origin = window.location.origin;
    const csv = toCsv(CSV_HEADERS, lines.map((l) => ({
      NMI: l.nmi ?? '',
      Retailer: l.retailer ?? '',
      Distributor: l.distributor ?? '',
      Tariff: l.tariff ?? '',
      State: l.state ?? '',
      Section: l.section,
      'Bill Item': l.bill_item,
      Amount: l.amount.toFixed(2),
      'Bill From': l.bill_from ?? '',
      'Bill To': l.bill_to ?? '',
      'Bill Link': `${origin}/bill-validation/bills/${l.bill_id}`,
    })));
    downloadCsv(csv, `unmapped-invoice-items-${new Date().toISOString().slice(0, 10)}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search bill item or NMI…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <select value={retailer} onChange={(e) => setRetailer(e.target.value)} className={SELECT_CLASS}>
          <option value="">All retailers</option>
          {retailers.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {report && (
          <span className="text-sm text-muted-foreground">
            {items.length.toLocaleString()} item{items.length === 1 ? '' : 's'} · {report.bills_scanned.toLocaleString()} bills scanned
          </span>
        )}
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setReloadToken((t) => t + 1)} disabled={loading} className="gap-1.5">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button variant="outline" onClick={handleExport} disabled={loading || !report?.lines.length} className="gap-1.5">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {report && report.retailers_without_map.length > 0 && (
        <Alert>
          <AlertDescription className="text-sm">
            <span className="font-medium">Not listed — retailer has no charge map yet:</span>{' '}
            {report.retailers_without_map.map((r) => `${r.retailer} (${r.bill_count} bill${r.bill_count === 1 ? '' : 's'})`).join(', ')}.
            Every line on those invoices is unmapped until the retailer&apos;s rows are added.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Retailer</TableHead>
                <TableHead>Section</TableHead>
                <TableHead>Bill Item</TableHead>
                <TableHead>Distributor / Tariff</TableHead>
                <TableHead>NMIs</TableHead>
                <TableHead className="text-right">Bills</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Scanning bills…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No unmapped items</TableCell></TableRow>
              ) : items.map((i) => (
                <TableRow key={`${i.retailer_id ?? i.retailer}|${i.section}|${i.bill_item}`}>
                  <TableCell className="font-medium">{i.retailer ?? '—'}</TableCell>
                  <TableCell><Badge variant="outline">{i.section}</Badge></TableCell>
                  <TableCell className="max-w-[240px]" title={i.bill_item}>{i.bill_item}</TableCell>
                  <TableCell className="text-sm">
                    {i.distributors.join(', ') || '—'}
                    {i.tariffs.length > 0 && (
                      <span className="block font-mono text-xs text-muted-foreground">{i.tariffs.join(', ')}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs" title={i.nmis.join(', ')}>
                    {i.nmis.slice(0, NMI_PREVIEW).join(', ')}
                    {i.nmis.length > NMI_PREVIEW && <span className="text-muted-foreground"> +{i.nmis.length - NMI_PREVIEW} more</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <a
                      href={`/bill-validation/bills/${i.last_bill_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      title="Open the most recent bill with this item"
                    >
                      {i.bill_count}
                    </a>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{money(i.total_amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onMap(i)}
                      disabled={!i.retailer_id}
                      title={i.retailer_id ? 'Add a mapping for this item' : 'Retailer not recognised'}
                      className="gap-1"
                    >
                      <Plus className="h-4 w-4" /> Map
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
